// Thin wrapper over the Web Speech API for reading Prof responses aloud.
// Italian voice is preferred; browser default is used when no Italian voice is
// installed. A new utterance always cancels in-flight speech so TTS controls do
// not overlap.

import { useCallback, useEffect, useRef, useState } from 'react';

export interface SpeakOptions {
  lang?: string;   // default 'it-IT'
  rate?: number;   // default DEFAULT_FALLBACK_SPEECH_RATE
  pitch?: number;  // default 1.0
  volume?: number; // default 1.0
}

export const DEFAULT_FALLBACK_SPEECH_RATE = 0.92;
export const MAX_FALLBACK_SPEECH_CHUNK_CHARS = 180;

export interface UseSpeechSynthesisReturn {
  supported:  boolean;
  speaking:   boolean;
  error:      string | null;
  speak:      (text: string, opts?: SpeakOptions) => Promise<void>;
  stop:       () => void;
  clearError: () => void;
}

// Preprocess Prof-authored markdown for speech: strip bold/italic markers,
// code fences, [[cite:ID]]/[[image:...]] tokens, and collapse newlines into
// sentence breaks. The goal is not phonetic perfection, just readable speech.
export function cleanForSpeech(raw: string): string {
  return raw
    .replace(/^\s*\[\[image:[^\]]+\]\]\s*(?:\r?\n|$)/gim, '')
    .replace(/\[\[image:[^\]]+\]\]/g, '')
    .replace(/\[\[cite:[^\]]+\]\]/g, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/(^|\s)\*([^*\s][^*]*)\*(\s|$)/g, '$1$2$3')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^#+\s+/gm, '')
    .replace(/^[-\u2022]\s+/gm, '')
    .replace(/\s+([:;,.!?])/g, '$1')
    .replace(/\n{2,}/g, '. ')
    .replace(/\n/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function splitSpeechIntoChunks(
  text: string,
  maxChars = MAX_FALLBACK_SPEECH_CHUNK_CHARS,
): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];

  const sentences = trimmed.match(/[^.!?]+[.!?]+|[^.!?]+$/g)
    ?.map((sentence) => sentence.trim())
    .filter(Boolean) ?? [trimmed];
  const speechUnits = sentences.flatMap((sentence) => {
    if (sentence.length <= maxChars) return [sentence];
    const clauses = sentence.match(/[^,;:]+[,;:]|[^,;:]+$/g)
      ?.map((clause) => clause.trim())
      .filter(Boolean) ?? [sentence];
    return clauses.length > 1 ? clauses : [sentence];
  });
  const chunks: string[] = [];
  let current = '';

  for (const unit of speechUnits) {
    const candidate = current ? `${current} ${unit}` : unit;
    if (current && candidate.length > maxChars) {
      chunks.push(current);
      current = unit;
    } else {
      current = candidate;
    }
  }

  if (current) chunks.push(current);
  return chunks;
}

function pickItalianVoice(): SpeechSynthesisVoice | null {
  if (typeof window === 'undefined') return null;
  const voices = window.speechSynthesis.getVoices?.() ?? [];
  if (voices.length === 0) return null;
  return (
    voices.find((v) => v.lang === 'it-IT') ??
    voices.find((v) => v.lang?.startsWith('it')) ??
    null
  );
}

export function useSpeechSynthesis(): UseSpeechSynthesisReturn {
  const [supported] = useState<boolean>(
    () => typeof window !== 'undefined' && 'speechSynthesis' in window,
  );
  const [speaking, setSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ownUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const sequenceRef = useRef(0);
  const resolveSpeechRef = useRef<(() => void) | null>(null);
  // Deferred speak() handle — used to bail out of a pending start if stop()
  // or a new speak() lands before the post-cancel tick fires (see speak()).
  const startTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resolveActiveSpeech = useCallback(() => {
    const resolve = resolveSpeechRef.current;
    resolveSpeechRef.current = null;
    resolve?.();
  }, []);

  // R2-9 fix: getVoices() is async on Chromium/WebKit — the first call returns
  // [] until the engine fires `voiceschanged`. We never *block* on voices (the
  // utterance still speaks with u.lang='it-IT'), but warming the list here lets
  // pickItalianVoice() actually find an it-IT voice on the first tap instead of
  // silently falling back to the system default. A no-op where unsupported / in
  // jsdom (getVoices already returns synchronously in the test mock).
  useEffect(() => {
    if (!supported) return;
    const synth = window.speechSynthesis;
    // Prime the cache: some engines only populate getVoices() after it's been
    // called once, then fire `voiceschanged`.
    try { synth.getVoices(); } catch { /* noop */ }
    const onVoicesChanged = () => {
      try { synth.getVoices(); } catch { /* noop */ }
    };
    synth.addEventListener?.('voiceschanged', onVoicesChanged);
    return () => {
      synth.removeEventListener?.('voiceschanged', onVoicesChanged);
    };
  }, [supported]);

  useEffect(() => {
    return () => {
      if (startTimerRef.current != null) {
        clearTimeout(startTimerRef.current);
        startTimerRef.current = null;
      }
      if (ownUtteranceRef.current) {
        try { window.speechSynthesis.cancel(); } catch { /* noop */ }
      }
      resolveActiveSpeech();
    };
  }, [resolveActiveSpeech]);

  const speak = useCallback((text: string, opts: SpeakOptions = {}) => {
    if (!supported || !text) return Promise.resolve();
    resolveActiveSpeech();
    try {
      const chunks = splitSpeechIntoChunks(cleanForSpeech(text));
      sequenceRef.current += 1;
      const sequenceId = sequenceRef.current;
      window.speechSynthesis.cancel();
      if (chunks.length === 0) {
        setSpeaking(false);
        ownUtteranceRef.current = null;
        return Promise.resolve();
      }

      const voice = pickItalianVoice();
      const done = new Promise<void>((resolve) => {
        resolveSpeechRef.current = resolve;
      });
      chunks.forEach((chunk, index) => {
        const u = new SpeechSynthesisUtterance(chunk);
        u.lang = opts.lang ?? 'it-IT';
        u.rate = opts.rate ?? DEFAULT_FALLBACK_SPEECH_RATE;
        u.pitch = opts.pitch ?? 1.0;
        u.volume = opts.volume ?? 1.0;
        if (voice) u.voice = voice;

        u.onstart = () => {
          if (sequenceRef.current === sequenceId) setSpeaking(true);
        };
        u.onend = () => {
          if (sequenceRef.current === sequenceId && index === chunks.length - 1) {
            setSpeaking(false);
            ownUtteranceRef.current = null;
            resolveActiveSpeech();
          }
        };
        u.onerror = (e) => {
          if (sequenceRef.current !== sequenceId) return;
          const raw = (e as SpeechSynthesisErrorEvent).error ?? 'unknown';
          if (raw !== 'canceled' && raw !== 'interrupted') setError(raw);
          setSpeaking(false);
          ownUtteranceRef.current = null;
          resolveActiveSpeech();
        };

        ownUtteranceRef.current = u;
        window.speechSynthesis.speak(u);
      });

      // R2-9 fix: Chromium/WebKit can leave the engine in a *paused* state right
      // after cancel() (a long-standing platform bug), which silently swallows
      // the freshly-queued utterance — the visible symptom is "Ascolta does
      // nothing". resume() un-pauses the queue so the speak() above actually
      // runs. No-op when the engine isn't paused, and harmless under the test
      // mock (which doesn't expose resume()).
      try { window.speechSynthesis.resume?.(); } catch { /* noop */ }

      // Some browsers do not fire onstart reliably for short utterances.
      setSpeaking(true);
      return done;
    } catch (err) {
      setError(String(err));
      setSpeaking(false);
      ownUtteranceRef.current = null;
      resolveActiveSpeech();
      return Promise.resolve();
    }
  }, [resolveActiveSpeech, supported]);

  const stop = useCallback(() => {
    sequenceRef.current += 1;
    try { window.speechSynthesis.cancel(); } catch { /* noop */ }
    setSpeaking(false);
    ownUtteranceRef.current = null;
    resolveActiveSpeech();
  }, [resolveActiveSpeech]);

  const clearError = useCallback(() => setError(null), []);

  return { supported, speaking, error, speak, stop, clearError };
}
